import {useCallback, useContext, useMemo} from 'react';
import Router, {useRouter} from 'next/router';
import axios from 'axios';

// Types
import {DispatchContext, StateContext} from './index';
import {
	setActiveArtist,
	setClaims,
	setProfiles,
	setToken,
	setUser,
} from './actions';
import type {MyUser, Profile} from './types';
import type {Claim} from '@deezer/next/services/api/Starmania/requests/claim/types';

import storage, {
	cleanImpersonateStorageKey,
	cleanJWTStorageKey,
} from '@deezer/next/shared/utils/storage';
import {
	getUserDefaultRoute,
	hasFeature,
	isArtistManager,
} from '@deezer/next/shared/utils/user';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {cdp} from '@deezer/next/services/tracking/LogCenter';
import {Routes} from '@constants/routes';
import type {FeatureLabel} from '@deezer/next/services/types/features';
import {
	setLanguagePreference,
	setThemePreference,
} from '@deezer/next/services/storage/shared/effects';
import type {ThemePreference} from '@deezer/next/services/storage/shared/types';
import HttpStatusCode from '@deezer/next/services/types/HttpStatusCode';

let logoutInFlight: Promise<void> | null = null;
let fetchUserMeInFlight: Promise<{user: MyUser; claims: Claim[]}> | null = null;

const USER_ME_MAX_ATTEMPTS = 3;
const USER_ME_RETRY_DELAY_MS = 1000;

/**
 * The user resolution logs out when it fails, which revokes the token: a rate
 * limited (429) call is retried first instead of logging out on a transient
 * error.
 */
const retryWhenRateLimited = async <T>(request: () => Promise<T>) => {
	for (let attempt = 1; ; attempt++) {
		try {
			return await request();
		} catch (error) {
			const isRateLimited =
				axios.isAxiosError(error) &&
				error.response?.status === HttpStatusCode.TOO_MANY_REQUESTS;

			if (!isRateLimited || attempt >= USER_ME_MAX_ATTEMPTS) {
				throw error;
			}

			await new Promise(resolve =>
				setTimeout(resolve, USER_ME_RETRY_DELAY_MS * attempt),
			);
		}
	}
};

export const useStateContext = () => {
	return useContext(StateContext);
};

export const useStateDispatch = () => {
	return useContext(DispatchContext);
};

export const useUser = (): MyUser | undefined => {
	const {user} = useStateContext();

	return user;
};

export const useClaims = (): Claim[] => {
	const {claims} = useStateContext();

	return claims;
};

export const useActiveArtist = (): number | undefined => {
	const {artistId} = useStateContext();

	return artistId;
};

export const useProfiles = (): Profile[] => {
	const {profiles} = useStateContext();

	return profiles;
};

export const useAuthToken = (): string => {
	const {authToken} = useStateContext();

	return authToken;
};

export const useLogout = () => {
	const dispatch = useStateDispatch();
	const authToken = useAuthToken();

	return useCallback(
		async ({replace}: {replace: boolean}) => {
			if (logoutInFlight) {
				await logoutInFlight;
				// A request failing with a 401 once the token is revoked redirects to
				// the logout page, cancelling the navigation of the ongoing logout to
				// the home page: finish it from the logout page.
				if (Router.pathname === Routes.LOGOUT) {
					await Router.replace(Routes.HOME);
				}
				return;
			}

			logoutInFlight = (async () => {
				try {
					if (authToken) {
						try {
							await StarmaniaApi.auth.revokeToken();
						} catch {
							// ignore error as this means the user is already logged out
						}
					}

					cleanJWTStorageKey();
					cleanImpersonateStorageKey();

					dispatch(setToken(''));
					dispatch(setUser(undefined));
					dispatch(setClaims([]));
					dispatch(setProfiles([]));
					dispatch(setActiveArtist(undefined));

					cdp.logout();
					storage.clear();

					if (replace) {
						await Router.replace(Routes.HOME);
					} else {
						await Router.push(Routes.HOME);
					}
				} finally {
					logoutInFlight = null;
				}
			})();

			await logoutInFlight;
		},
		[authToken, dispatch],
	);
};

export const useFetchUserMe = () => {
	const dispatch = useStateDispatch();
	const authToken = useAuthToken();
	const handleLogout = useLogout();

	return useCallback(
		async (token?: string): Promise<{user: MyUser; claims: Claim[]}> => {
			const effectiveToken = token || authToken;

			if (!effectiveToken) {
				await handleLogout({replace: true});
				throw new Error('Missing auth token');
			}

			if (fetchUserMeInFlight) {
				return fetchUserMeInFlight;
			}

			fetchUserMeInFlight = (async () => {
				try {
					const [{data: userData}, {data: claims}] = await Promise.all([
						retryWhenRateLimited(() => StarmaniaApi.users.getMe()),
						retryWhenRateLimited(() => StarmaniaApi.claim.getClaims()),
					]);

					const redirectionRoute = getUserDefaultRoute(userData);

					const normalizedUser: MyUser = {
						...userData,
						uriRedirectionOnLogin:
							redirectionRoute === '/'
								? userData.uriRedirectionOnLogin // no rights user has '/' and is expected
								: redirectionRoute.startsWith('/')
									? redirectionRoute
									: `/${redirectionRoute}`,
					};

					const language = normalizedUser?.settings?.user?.language;
					const theme = normalizedUser?.settings?.user?.theme?.mode as
						ThemePreference | undefined;

					if (language) {
						setLanguagePreference(language);
					}

					if (theme) {
						setThemePreference(theme);
					}

					dispatch(setUser(normalizedUser));
					dispatch(setClaims(claims));

					if (isArtistManager(userData)) {
						const {data: profiles} = await retryWhenRateLimited(() =>
							StarmaniaApi.users.getMyProfiles(),
						);
						dispatch(setProfiles(profiles));
					} else {
						dispatch(setProfiles([]));
					}

					return {
						user: normalizedUser,
						claims,
					};
				} catch (err) {
					await handleLogout({replace: true});
					throw err;
				} finally {
					fetchUserMeInFlight = null;
				}
			})();

			return fetchUserMeInFlight;
		},
		[authToken, dispatch, handleLogout],
	);
};

export const useFeatureAccess = (feature: FeatureLabel) => {
	const user = useUser();
	return useMemo(() => hasFeature(user, feature), [feature, user]);
};

export const useLogin = () => {
	const {locale} = useRouter();
	const dispatch = useStateDispatch();
	const fetchUserMe = useFetchUserMe();

	return useCallback(
		async (
			email: string,
			password: string,
		): Promise<{user: MyUser; claims: Claim[]; lang?: string}> => {
			try {
				const {
					data: {token},
				} = await StarmaniaApi.auth.getToken(email, password);

				dispatch(setToken(token));
				dispatch(setActiveArtist(undefined));

				const response = await fetchUserMe(token);
				cdp.identifyUser(response.user);

				const remoteLanguage = response.user?.settings?.user?.language;
				const remoteTheme = response.user?.settings?.user?.theme?.mode as
					ThemePreference | undefined;

				if (!remoteLanguage && locale) {
					await StarmaniaApi.users.postSetting('user', {
						...response.user?.settings?.user,
						language: locale,
					});
					setLanguagePreference(locale);
				} else if (remoteLanguage) {
					setLanguagePreference(remoteLanguage);
				}

				if (remoteTheme) {
					setThemePreference(remoteTheme);
				}

				return {
					...response,
					lang: remoteLanguage || locale,
				};
			} catch (e: any) {
				if (e.response && e.response.data.errors && e.response.data.errors[0]) {
					throw e.response.data.errors[0].detail;
				}

				throw new Error('Unknown error, please try again');
			}
		},
		[dispatch, fetchUserMe, locale],
	);
};
